import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { addNotification } from '../../../store/uiSlice';
import apiService from '../../../services/api';
import {
  WritingStyle,
  IllustrationStyle,
  StyleAnalytics,
  StyleFormData,
  ToggleCategory,
  PopularCategory,
} from './types';

export function useStylesData() {
  const dispatch = useDispatch<AppDispatch>();

  const [writingGenres, setWritingGenres] = useState<WritingStyle[]>([]);
  const [writingAuthors, setWritingAuthors] = useState<WritingStyle[]>([]);
  const [illustrationStyles, setIllustrationStyles] = useState<IllustrationStyle[]>([]);
  const [illustrationArtists, setIllustrationArtists] = useState<IllustrationStyle[]>([]);
  const [analytics, setAnalytics] = useState<StyleAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [popularCategory, setPopularCategory] = useState<PopularCategory>({ writing: 'genre', illustration: 'style' });

  const loadWritingGenres = async () => {
    try {
      const response = await apiService.getAIWritingGenres();
      const stylesData = response || [];
      const mappedStyles = stylesData.map((style: any, index: number) => ({
        id: style.id || index,
        name: style.name || style.display_name || 'Untitled Genre',
        description: style.description || '',
        displayDescription: style.display_description || '',
        sampleExcerpt: style.sample_excerpt || '',
        prompt: style.prompt || style.grok_prompt || style.style_prompt_template || '',
        styleDNA: style.style_prompt_template || style.prompt || '',
        transformationFormula: style.vocabulary_prompt || '',
        truthIntegration: style.tone_guidance || '',
        category: 'Genre',
        isActive: style.is_active !== false,
        usageCount: style.usage_count || 0,
        tags: style.tags || [],
        createdAt: style.created_at || new Date().toISOString(),
        updatedAt: style.updated_at || new Date().toISOString(),
        modelPreference: style.modelPreference ?? style.model_preference ?? null,
      }));
      setWritingGenres(mappedStyles);
    } catch (error) {
      console.error('Failed to load writing genres:', error);
      setWritingGenres([]);
    }
  };

  const loadWritingAuthors = async () => {
    try {
      const response = await apiService.getAIWritingAuthors();
      const authorsData = response || [];
      const mappedAuthors = authorsData.map((author: any, index: number) => ({
        id: author.id || index,
        name: author.name || author.display_name || 'Untitled Author',
        description: author.description || '',
        displayDescription: author.display_description || '',
        sampleExcerpt: author.sample_excerpt || '',
        prompt: author.prompt || author.grok_prompt || author.style_prompt_template || '',
        styleDNA: author.styleDNA || author.style_dna_summary || '',
        transformationFormula: author.transformationFormula || author.transformation_formula || '',
        truthIntegration: author.truthIntegration || author.truth_integration_approach || '',
        category: 'Author',
        isActive: author.is_active !== false,
        usageCount: author.usage_count || 0,
        tags: author.tags || [],
        createdAt: author.created_at || new Date().toISOString(),
        updatedAt: author.updated_at || new Date().toISOString(),
        modelPreference: author.modelPreference ?? author.model_preference ?? null,
      }));
      setWritingAuthors(mappedAuthors);
    } catch (error) {
      console.error('Failed to load writing authors:', error);
      setWritingAuthors([]);
    }
  };

  const loadIllustrationArtStyles = async () => {
    try {
      const response = await apiService.getAIIllustrationArtStyles();
      const stylesData = response || [];
      const mappedStyles = stylesData.map((style: any, index: number) => ({
        id: style.id || index,
        name: style.name || style.display_name || 'Untitled Style',
        description: style.description || '',
        displayDescription: style.display_description || '',
        prompt: style.prompt || style.grok_prompt || style.base_prompt_template || '',
        styleDNA: style.prompt || '',
        visualApproach: style.character_prompt_additions || '',
        promptConstructionFormula: style.scene_prompt_additions || '',
        truthIntegration: typeof style.mood_integration_prompts === 'string'
          ? style.mood_integration_prompts
          : (style.mood_integration_prompts ? JSON.stringify(style.mood_integration_prompts) : ''),
        category: 'Style',
        isActive: style.is_active !== false,
        usageCount: style.usage_count || 0,
        tags: style.tags || [],
        createdAt: style.created_at || new Date().toISOString(),
        updatedAt: style.updated_at || new Date().toISOString(),
        modelPreference: style.modelPreference ?? style.model_preference ?? null,
      }));
      setIllustrationStyles(mappedStyles);
    } catch (error) {
      console.error('Failed to load illustration art styles:', error);
      setIllustrationStyles([]);
    }
  };

  const loadIllustrationArtists = async () => {
    try {
      const response = await apiService.getAIIllustrationArtists();
      const artistsData = response || [];
      const mappedArtists = artistsData.map((artist: any, index: number) => ({
        id: artist.id || index,
        name: artist.name || artist.display_name || 'Untitled Artist',
        description: artist.description || '',
        displayDescription: artist.display_description || '',
        prompt: artist.prompt || artist.grok_prompt || artist.base_prompt_template || '',
        styleDNA: artist.prompt || '',
        visualApproach: artist.character_prompt_additions || '',
        promptConstructionFormula: artist.scene_prompt_additions || '',
        truthIntegration: typeof artist.mood_integration_prompts === 'string'
          ? artist.mood_integration_prompts
          : (artist.mood_integration_prompts ? JSON.stringify(artist.mood_integration_prompts) : ''),
        category: 'Artist',
        isActive: artist.is_active !== false,
        usageCount: artist.usage_count || 0,
        tags: artist.tags || [],
        createdAt: artist.created_at || new Date().toISOString(),
        updatedAt: artist.updated_at || new Date().toISOString(),
        modelPreference: artist.modelPreference ?? artist.model_preference ?? null,
      }));
      setIllustrationArtists(mappedArtists);
    } catch (error) {
      console.error('Failed to load illustration artists:', error);
      setIllustrationArtists([]);
    }
  };

  const loadPopularCategory = async () => {
    try {
      const data = await apiService.getPopularCategorySettings();
      if (data) {
        setPopularCategory({ writing: data.writing || 'genre', illustration: data.illustration || 'style' });
      }
    } catch (err) {
      console.warn('Failed to load popular category settings, using defaults');
    }
  };

  const generateCategoryData = () => [
    { category: 'Writing Genres', count: writingGenres.length },
    { category: 'Writing Authors', count: writingAuthors.length },
    { category: 'Illustration Styles', count: illustrationStyles.length },
    { category: 'Illustration Artists', count: illustrationArtists.length },
  ];

  const generateUsageOverTime = () => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return {
        date: date.toISOString().split('T')[0],
        writing: Math.floor(Math.random() * 100) + 50,
        illustration: Math.floor(Math.random() * 50) + 25,
      };
    }).reverse();
    return last7Days;
  };

  const loadAnalytics = async () => {
    try {
      const response = await apiService.getAIStylesAnalytics();
      const analyticsData = response?.data || response;

      const totalWriting = writingGenres.length + writingAuthors.length;
      const activeWriting = writingGenres.filter(s => s.isActive).length + writingAuthors.filter(s => s.isActive).length;
      const totalIllustration = illustrationStyles.length + illustrationArtists.length;
      const activeIllustration = illustrationStyles.filter(s => s.isActive).length + illustrationArtists.filter(s => s.isActive).length;

      setAnalytics({
        totalWritingStyles: analyticsData?.totalWritingStyles || totalWriting,
        activeWritingStyles: analyticsData?.activeWritingStyles || activeWriting,
        totalIllustrationStyles: analyticsData?.totalIllustrationStyles || totalIllustration,
        activeIllustrationStyles: analyticsData?.activeIllustrationStyles || activeIllustration,
        avgUsageCount: analyticsData?.avgUsageCount || 0,
        avgRating: analyticsData?.avgRating || 0,
        stylesByCategory: analyticsData?.stylesByCategory || generateCategoryData(),
        usageOverTime: analyticsData?.usageOverTime || generateUsageOverTime(),
        topStyles: analyticsData?.topStyles || [...writingGenres, ...writingAuthors, ...illustrationStyles, ...illustrationArtists].slice(0, 5),
      });
    } catch (error) {
      console.error('Failed to load styles analytics:', error);
      const allStyles = [...writingGenres, ...writingAuthors, ...illustrationStyles, ...illustrationArtists];
      setAnalytics({
        totalWritingStyles: writingGenres.length + writingAuthors.length,
        activeWritingStyles: writingGenres.filter(s => s.isActive).length + writingAuthors.filter(s => s.isActive).length,
        totalIllustrationStyles: illustrationStyles.length + illustrationArtists.length,
        activeIllustrationStyles: illustrationStyles.filter(s => s.isActive).length + illustrationArtists.filter(s => s.isActive).length,
        avgUsageCount: allStyles.reduce((acc, s) => acc + s.usageCount, 0) / (allStyles.length || 1),
        avgRating: 0,
        stylesByCategory: generateCategoryData(),
        usageOverTime: generateUsageOverTime(),
        topStyles: allStyles.slice(0, 5),
      });
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([
      loadWritingGenres(),
      loadWritingAuthors(),
      loadIllustrationArtStyles(),
      loadIllustrationArtists(),
    ]);
    setLoading(false);
    dispatch(addNotification({ type: 'success', message: 'AI Styles data refreshed' }));
  };

  const handleToggleStatus = async (id: number, currentActive: boolean, category: ToggleCategory) => {
    const nextActive = !currentActive;
    try {
      switch (category) {
        case 'writing-genre':
          await apiService.toggleAIWritingGenre(id, nextActive);
          break;
        case 'writing-author':
          await apiService.toggleAIWritingAuthor(id, nextActive);
          break;
        case 'illustration-style':
          await apiService.toggleAIIllustrationStyle(id, nextActive);
          break;
        case 'illustration-artist':
          await apiService.toggleAIIllustrationArtist(id, nextActive);
          break;
      }
      dispatch(addNotification({ type: 'success', message: nextActive ? 'Style activated' : 'Style deactivated' }));
      await handleRefresh();
    } catch (error: any) {
      const msg = error?.message || String(error);
      const is409 = msg.includes('409') || /max.*active|active.*max/i.test(msg);
      dispatch(addNotification({
        type: 'error',
        message: is409 ? 'Maximum 4 active styles allowed per category.' : msg,
      }));
    }
  };

  const handleDeleteStyle = async (styleId: number, type: 'writing' | 'illustration') => {
    if (!window.confirm(`Are you sure you want to delete this ${type} style?`)) return;

    try {
      const apiCall = type === 'writing'
        ? apiService.deleteAIWritingStyle
        : apiService.deleteAIIllustrationStyle;
      await apiCall(styleId);
      dispatch(addNotification({
        type: 'success',
        message: `${type === 'writing' ? 'Writing' : 'Illustration'} style deleted successfully`,
      }));
      await handleRefresh();
    } catch (error) {
      console.error('Delete error:', error);
      dispatch(addNotification({
        type: 'warning',
        message: 'API not configured - delete functionality not available',
      }));
    }
  };

  const handleExport = async (type: 'writing' | 'illustration' | 'moods') => {
    try {
      dispatch(addNotification({ type: 'info', message: `Generating ${type} styles PDF export...` }));

      let blob: Blob;
      let filename: string;

      switch (type) {
        case 'writing':
          blob = await apiService.exportAIWritingStyles();
          filename = 'ai-writing-styles-export.pdf';
          break;
        case 'illustration':
          blob = await apiService.exportAIIllustrationStyles();
          filename = 'ai-illustration-styles-export.pdf';
          break;
        case 'moods':
          blob = await apiService.exportAIMoods();
          filename = 'ai-moods-export.pdf';
          break;
        default:
          throw new Error('Invalid export type');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(addNotification({ type: 'success', message: `${type} styles exported successfully` }));
    } catch (error) {
      console.error('Export error:', error);
      dispatch(addNotification({ type: 'error', message: `Failed to export ${type} styles. Please try again.` }));
    }
  };

  const handleUpdateModelPreference = async (
    id: number,
    category: ToggleCategory,
    modelPreference: string | null,
  ) => {
    const applyLocal = (list: any[], setter: (v: any) => void) => {
      setter(list.map((i: any) => (i.id === id ? { ...i, modelPreference } : i)));
    };
    try {
      switch (category) {
        case 'writing-genre':
          applyLocal(writingGenres, setWritingGenres);
          await apiService.updateAIWritingStyle(id, { modelPreference });
          break;
        case 'writing-author':
          applyLocal(writingAuthors, setWritingAuthors);
          await apiService.updateAIWritingAuthor(id, { modelPreference });
          break;
        case 'illustration-style':
          applyLocal(illustrationStyles, setIllustrationStyles);
          await apiService.updateAIIllustrationStyle(id, { modelPreference });
          break;
        case 'illustration-artist':
          applyLocal(illustrationArtists, setIllustrationArtists);
          await apiService.updateAIIllustrationStyle(id, { modelPreference });
          break;
      }
      dispatch(addNotification({
        type: 'success',
        message: modelPreference ? `Model override set to ${modelPreference}` : 'Model override cleared (using global default)',
      }));
    } catch (error: any) {
      dispatch(addNotification({
        type: 'error',
        message: error?.message || 'Failed to update model preference',
      }));
      // Reload to undo optimistic update
      await handleRefresh();
    }
  };

  const handleTogglePopularCategory = async (type: 'writing' | 'illustration', value: string) => {
    try {
      await apiService.updatePopularCategory({ [type]: value });
      setPopularCategory(prev => ({ ...prev, [type]: value }));
      dispatch(addNotification({ type: 'success', message: `"Most Popular" badge now assigned to ${value} category` }));
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: error?.message || 'Failed to update popular category' }));
    }
  };

  const combineStructuredToPrompt = (data: StyleFormData, type: string): StyleFormData => {
    const hasStructuredData = data.styleDNA || data.transformationFormula ||
                               data.visualApproach || data.promptConstructionFormula ||
                               data.truthIntegration;
    if (!hasStructuredData) return data;

    const sections: string[] = [];
    if (data.styleDNA) sections.push(`[STYLE DNA]\n${data.styleDNA}`);

    if (type.startsWith('writing')) {
      if (data.transformationFormula) sections.push(`[TRANSFORMATION FORMULA]\n${data.transformationFormula}`);
    } else {
      if (data.visualApproach) sections.push(`[VISUAL APPROACH]\n${data.visualApproach}`);
      if (data.promptConstructionFormula) sections.push(`[PROMPT CONSTRUCTION FORMULA]\n${data.promptConstructionFormula}`);
    }

    if (data.truthIntegration) sections.push(`[TRUTH INTEGRATION]\n${data.truthIntegration}`);

    return { ...data, prompt: sections.join('\n\n') };
  };

  const handleSubmitStyle = async (
    data: StyleFormData,
    editingStyle: WritingStyle | IllustrationStyle | null,
    editingType: string,
    onSuccess: () => void,
  ) => {
    const transformedData = combineStructuredToPrompt(data, editingType);
    try {
      if (editingStyle) {
        if (editingType.startsWith('writing')) {
          const isAuthor = (editingStyle as any).category === 'Author';
          if (isAuthor) {
            await apiService.updateAIWritingAuthor(editingStyle.id, transformedData);
          } else {
            await apiService.updateAIWritingStyle(editingStyle.id, transformedData);
          }
        } else {
          await apiService.updateAIIllustrationStyle(editingStyle.id, transformedData);
        }
      } else {
        if (editingType.startsWith('writing')) {
          await apiService.createAIWritingStyle(transformedData);
        } else {
          await apiService.createAIIllustrationStyle(transformedData);
        }
      }
      dispatch(addNotification({
        type: 'success',
        message: `${editingType.startsWith('writing') ? 'Writing' : 'Illustration'} style ${editingStyle ? 'updated' : 'created'} successfully`,
      }));
      onSuccess();
      await handleRefresh();
    } catch (error) {
      console.error('Form submission error:', error);
      dispatch(addNotification({
        type: 'warning',
        message: `API not configured - ${editingType} style ${editingStyle ? 'update' : 'creation'} functionality not available`,
      }));
      onSuccess();
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadWritingGenres(),
          loadWritingAuthors(),
          loadIllustrationArtStyles(),
          loadIllustrationArtists(),
          loadPopularCategory(),
        ]);
      } catch (error) {
        console.error('Error loading AI styles:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Load analytics when style data changes
  useEffect(() => {
    if (writingGenres.length > 0 || writingAuthors.length > 0 ||
        illustrationStyles.length > 0 || illustrationArtists.length > 0) {
      loadAnalytics();
    }
  }, [writingGenres, writingAuthors, illustrationStyles, illustrationArtists]);

  // Get data array by category key
  const getDataForCategory = (categoryKey: string) => {
    switch (categoryKey) {
      case 'writing-genres': return writingGenres;
      case 'writing-authors': return writingAuthors;
      case 'illustration-art-styles': return illustrationStyles;
      case 'illustration-artists': return illustrationArtists;
      default: return [];
    }
  };

  return {
    writingGenres,
    writingAuthors,
    illustrationStyles,
    illustrationArtists,
    analytics,
    loading,
    popularCategory,
    handleRefresh,
    handleToggleStatus,
    handleDeleteStyle,
    handleExport,
    handleTogglePopularCategory,
    handleUpdateModelPreference,
    handleSubmitStyle,
    getDataForCategory,
  };
}
